import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import Navbar from "./Navbar";
import { canchaService } from "../../services/canchaService";
import { reservaService } from "../../services/reservaService";
import { userService } from "../../services/userService";


export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('establecimientos');
  const [establecimientos, setEstablecimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedEst, setSelectedEst] = useState(null);
  const [canchas, setCanchas] = useState([]);
  const [ingresos, setIngresos] = useState({}); // { [id]: monto }
  // Usuarios
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");

  // Finanzas globales (gráfico de barras)
  const [globalFinanceData, setGlobalFinanceData] = useState([]);
  const [loadingGlobalFinance, setLoadingGlobalFinance] = useState(false);
  const [errorGlobalFinance, setErrorGlobalFinance] = useState(null);
  // Cargar usuarios cuando se selecciona la pestaña
  useEffect(() => {
    if (activeTab === 'usuarios') {
      setUsersLoading(true);
      setUsersError("");
      userService.getAllUsers()
        .then((res) => {
          console.log('Respuesta de getAllUsers:', res);
          // Ajuste flexible según la estructura
          if (Array.isArray(res)) {
            setUsers(res);
          } else if (Array.isArray(res?.data)) {
            setUsers(res.data);
          } else if (Array.isArray(res?.users)) {
            setUsers(res.users);
          } else {
            setUsers([]);
          }
        })
        .catch(() => setUsersError("Error al cargar usuarios"))
        .finally(() => setUsersLoading(false));
    }
  }, [activeTab]);
  // Cambiar rol de usuario
  const handleRoleChange = async (userId, newRole) => {
    try {
      await userService.changeUserRole(userId, newRole);
      setUsers((prev) => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch {
      alert('Error al cambiar el rol');
    }
  };

  useEffect(() => {
    const fetchEsts = async () => {
      setLoading(true);
      setError("");
      try {
        // Lista de establecimientos
        const res = await canchaService.getAllEstablecimientos();
        const lista = res?.data || res || [];
        setEstablecimientos(Array.isArray(lista) ? lista : []);

        // Ingresos por establecimiento (si el endpoint existe)
        const ingresosObj = {};
        for (const est of Array.isArray(lista) ? lista : []) {
          try {
            // Espera: reservaService.getReporteIngresosEstablecimiento(estId) -> { total: number }
            const rep = await reservaService.getReporteIngresosEstablecimiento(
              est.id
            );
            ingresosObj[est.id] = Number(rep?.total ?? rep?.ingresos ?? 0);
          } catch {
            ingresosObj[est.id] = 0;
          }
        }
        setIngresos(ingresosObj);
      } catch (e) {
        console.error(e);
        setError("Error al cargar establecimientos");
      } finally {
        setLoading(false);
      }
    };
    fetchEsts();

    // Cargar finanzas globales (gráfico de barras)
    const fetchGlobalFinance = async () => {
      setLoadingGlobalFinance(true);
      setErrorGlobalFinance(null);
      try {
        if (reservaService.getReporteIngresosGlobal) {
          const res = await reservaService.getReporteIngresosGlobal();
          setGlobalFinanceData(res.data || res);
        }
      } catch (e) {
        setErrorGlobalFinance("Error al cargar finanzas globales");
      } finally {
        setLoadingGlobalFinance(false);
      }
    };
    fetchGlobalFinance();
  }, []);

  const handleVerCanchas = async (estId) => {
    setSelectedEst(estId);
    setCanchas([]);
    try {
      const res = await canchaService.getCanchasByEstablecimiento(estId);
      const lista = res?.canchas || res?.data?.canchas || [];
      setCanchas(Array.isArray(lista) ? lista : []);
    } catch (e) {
      console.error(e);
      setCanchas([]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-6 text-center text-indigo-800">
          Dashboard Administrador
        </h1>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 justify-center">
            <button
              onClick={() => setActiveTab('establecimientos')}
              className={`py-2 px-4 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'establecimientos'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Establecimientos
            </button>
            <button
              onClick={() => setActiveTab('usuarios')}
              className={`py-2 px-4 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'usuarios'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Usuarios
            </button>
            <button
              onClick={() => setActiveTab('finanzas')}
              className={`py-2 px-4 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'finanzas'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Finanzas
            </button>
          </nav>
        </div>

        {/* Contenido de cada tab */}
        {activeTab === 'establecimientos' && (
          <>
            {/* KPIs simples */}
            <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="bg-green-100 border border-green-300 rounded-xl p-6 flex-1 text-center shadow">
                <div className="text-2xl font-bold text-green-800">
                  {establecimientos.length}
                </div>
                <div className="text-lg text-green-700">
                  Establecimientos registrados
                </div>
              </div>
            </div>

            {/* Tabla de establecimientos */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-xl shadow">
                <thead className="bg-gradient-to-r from-green-100 to-blue-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-green-800 uppercase">
                      Nombre
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-green-800 uppercase">
                      Dirección
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-green-800 uppercase">
                      Teléfono
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-green-800 uppercase">
                      Ingresos
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-green-800 uppercase">
                      Canchas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {establecimientos.map((est) => (
                    <tr key={est.id} className="border-b hover:bg-green-50">
                      <td className="px-4 py-2 font-semibold">
                        {est.nombre_negocio || est.name || "-"}
                      </td>
                      <td className="px-4 py-2">{est.direccion || "-"}</td>
                      <td className="px-4 py-2">{est.telefono || "-"}</td>
                      <td className="px-4 py-2 font-bold text-green-700">
                        S/ {Number(ingresos[est.id] || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                          onClick={() => handleVerCanchas(est.id)}
                        >
                          Ver canchas
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detalle de canchas del establecimiento seleccionado */}
            {selectedEst && (
              <div className="mt-8 bg-white rounded-xl shadow p-6">
                <h2 className="text-2xl font-bold mb-4 text-green-800">
                  Canchas de{" "}
                  {establecimientos.find((e) => e.id === selectedEst)
                    ?.nombre_negocio || "-"}
                </h2>
                {canchas.length === 0 ? (
                  <div className="text-gray-500">
                    No hay canchas registradas.
                  </div>
                ) : (
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {canchas.map((c) => (
                      <li
                        key={c.id}
                        className="bg-green-50 border border-green-200 rounded-lg p-4 shadow"
                      >
                        <div className="font-bold text-green-900 text-lg mb-1">
                          {c.nombre}
                        </div>
                        <div className="text-gray-700">Tipo: {c.tipo}</div>
                        <div className="text-gray-700">
                          Precio/hora: S/ {c.precio_por_hora ?? c.precio_hora}
                        </div>
                        <div className="text-gray-700">
                          Descripción: {c.descripcion}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'usuarios' && (
          <div className="bg-white rounded-xl shadow p-8">
            <h2 className="text-2xl font-bold mb-6 text-indigo-800 text-center">Gestión de Usuarios</h2>
            {usersLoading ? (
              <div className="text-center">Cargando usuarios...</div>
            ) : usersError ? (
              <div className="text-red-500 text-center">{usersError}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol Actual</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cambiar Rol</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Registro</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            className="text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="cliente">Cliente</option>
                            <option value="establecimiento">Establecimiento</option>
                            <option value="administrador">Administrador</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'finanzas' && (
          <div className="bg-white rounded-xl shadow p-8">
            <h2 className="text-2xl font-bold mb-6 text-indigo-800 text-center">Finanzas</h2>
            {/* Gráfico de barras de ingresos globales por mes */}
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-2 text-indigo-800 flex items-center gap-2">
                <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h1m2 0h13M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" /></svg>
                Ingresos Globales (por mes)
              </h3>
              {errorGlobalFinance && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{errorGlobalFinance}</div>
              )}
              {loadingGlobalFinance ? (
                <div className="flex items-center justify-center h-32">
                  <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-gray-300 border-t-gray-600" role="status" aria-label="Cargando" />
                  <p className="ml-2">Cargando finanzas globales...</p>
                </div>
              ) : Array.isArray(globalFinanceData) && globalFinanceData.length > 0 ? (
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl shadow p-6 mb-4 border border-indigo-100">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                      data={globalFinanceData.map((row) => ({ mes: row.mes, ingresos: Number(row.ingresos ?? row.total ?? row.suma ?? 0) }))}
                      margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="mes" tick={{ fontSize: 13, fill: '#6366f1', fontWeight: 600 }} />
                      <YAxis tickFormatter={(v) => `S/ ${v.toLocaleString()}`} tick={{ fontSize: 13, fill: '#4f46e5', fontWeight: 600 }} />
                      <Tooltip contentStyle={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8 }} formatter={(v) => `S/ ${v.toLocaleString()}`} labelFormatter={(v) => `Mes: ${v}`} />
                      <Legend wrapperStyle={{ fontWeight: 600, color: '#4f46e5' }} />
                      <Bar dataKey="ingresos" fill="#6366f1" name="Ingresos Globales (PEN)" radius={[8, 8, 0, 0]} barSize={38} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-gray-500">No hay datos globales de ingresos.</div>
              )}
            </div>
            {/* Resumen global */}
            <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-center gap-4">
              <div className="bg-blue-100 border border-blue-300 rounded-xl p-6 flex-1 text-center shadow">
                <div className="text-2xl font-bold text-blue-800">
                  S/ {Object.values(ingresos).reduce((acc, val) => acc + Number(val || 0), 0).toFixed(2)}
                </div>
                <div className="text-lg text-blue-700">Total ingresos (todos los establecimientos)</div>
              </div>
            </div>
            {/* Tabla de ingresos por establecimiento */}
            <div className="overflow-x-auto mb-8">
              <table className="min-w-full bg-white rounded-xl shadow">
                <thead className="bg-gradient-to-r from-blue-100 to-green-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-blue-800 uppercase">Establecimiento</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-blue-800 uppercase">Ingresos</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-blue-800 uppercase">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {establecimientos.map((est) => (
                    <tr key={est.id} className="border-b hover:bg-blue-50">
                      <td className="px-4 py-2 font-semibold">{est.nombre_negocio || est.name || '-'}</td>
                      <td className="px-4 py-2 font-bold text-blue-700">S/ {Number(ingresos[est.id] || 0).toFixed(2)}</td>
                      <td className="px-4 py-2">
                        <button
                          className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                          onClick={() => setSelectedEst(est.id)}
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Detalle de ingresos del establecimiento seleccionado */}
            {selectedEst && (
              <div className="mt-8 bg-gray-50 rounded-xl shadow p-6">
                <h3 className="text-xl font-bold mb-4 text-blue-800">
                  Detalle de ingresos de {establecimientos.find(e => e.id === selectedEst)?.nombre_negocio || '-'}
                </h3>
                {/* Aquí podrías mostrar ingresos por cancha, por fecha, etc. Por ahora solo el total: */}
                <div className="text-lg text-blue-700 mb-2">
                  Total: <span className="font-bold">S/ {Number(ingresos[selectedEst] || 0).toFixed(2)}</span>
                </div>
                <button
                  className="mt-2 px-4 py-1 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                  onClick={() => setSelectedEst(null)}
                >
                  Cerrar detalle
                </button>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="text-center">Cargando...</div>
        )}
        {error && (
          <div className="text-red-500 text-center">{error}</div>
        )}
      </div>
    </div>
  );
}
